// V2 /app API client (BRIEFS C2.4).
//
// Its OWN axios instance — never the old api/axios.js, whose 401 handler throws the user
// to /login. Here:
//   - the short access token (60 min) lives in memory only;
//   - the long device token lives in localStorage["v2_device_token"] (permanent login);
//   - on a 401 the client calls /auth/refresh ONCE and retries the request;
//   - only when the server answers that the device is signed out (admin revoked it, or the
//     user was made inactive) is the device token cleared and the login screen shown.
//     A network error during refresh never signs Harsh out (the laptop may be offline).
import axios from 'axios';

const ROOT = process.env.REACT_APP_API_URL || '';
export const V2_BASE = `${ROOT}/api/v2`;
export const DEVICE_TOKEN_KEY = 'v2_device_token';

// ------------------------------------------------------------------ token store

let accessToken = null;
const listeners = new Set();

function readDeviceToken() {
  try { return localStorage.getItem(DEVICE_TOKEN_KEY); } catch { return null; }
}

export const tokens = {
  get access() { return accessToken; },
  get device() { return readDeviceToken(); },
  set(access, device) {
    accessToken = access || null;
    if (device) {
      try { localStorage.setItem(DEVICE_TOKEN_KEY, device); } catch { /* private mode */ }
    }
  },
  clear() {
    accessToken = null;
    try { localStorage.removeItem(DEVICE_TOKEN_KEY); } catch { /* ignore */ }
  },
};

// AppAuthContext listens here: {type:'user', user} after a refresh, {type:'signedOut'}.
export function onAuthEvent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit(ev) { listeners.forEach((fn) => { try { fn(ev); } catch { /* ignore */ } }); }

export class SignedOutError extends Error {
  constructor() { super('device_signed_out'); this.signedOut = true; }
}

// ------------------------------------------------------------------ refresh (one at a time)

let refreshing = null;

export function refreshAccess() {
  if (refreshing) return refreshing;
  const device = readDeviceToken();
  if (!device) {
    accessToken = null;
    return Promise.reject(new SignedOutError());
  }
  refreshing = axios
    .post(`${V2_BASE}/auth/refresh`, { device_token: device })
    .then((res) => {
      accessToken = res.data.access_token;
      emit({ type: 'user', user: res.data.user });
      return res.data;
    })
    .catch((err) => {
      if (err.response && err.response.status === 401) {
        tokens.clear();
        emit({ type: 'signedOut' });
        throw new SignedOutError();
      }
      throw err; // offline / server error: keep the device token, try again later
    })
    .finally(() => { refreshing = null; });
  return refreshing;
}

// ------------------------------------------------------------------ the V2 axios instance

const v2 = axios.create({ baseURL: V2_BASE });

v2.interceptors.request.use(async (config) => {
  if (!accessToken && readDeviceToken() && !config._noAuth) {
    try { await refreshAccess(); } catch { /* the request will 401 and be handled below */ }
  }
  if (accessToken && !config._noAuth) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

v2.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config || {};
    if (err.response && err.response.status === 401 && !config._retried && !config._noAuth) {
      config._retried = true;
      accessToken = null;
      await refreshAccess(); // throws SignedOutError when the device is signed out
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
      return v2(config);
    }
    return Promise.reject(err);
  }
);

export default v2;

// ------------------------------------------------------------------ calls

export async function deviceLogin(username, password, deviceName) {
  const res = await v2.post(
    '/auth/device-login',
    { username, password, device_name: deviceName },
    { _noAuth: true }
  );
  tokens.set(res.data.access_token, res.data.device_token);
  return res.data;
}

export const getMe = () => v2.get('/auth/me').then((r) => r.data);
export const getMySettings = () => v2.get('/me/settings').then((r) => r.data);
export const getDashboard = () => v2.get('/dashboard').then((r) => r.data);
// One day's questions for the screen a bar of "Questions solved — last 30 days" opens.
export const getDayQuestions = (day) => v2.get(`/dashboard/day/${day}`).then((r) => r.data);
export const postActivity = (events) => v2.post('/activity', { events }).then((r) => r.data);
// Notes (C4.3)
export const getChapters = () => v2.get('/chapters').then((r) => r.data);
export const getChapterNotes = (chapterId, { preview = false } = {}) =>
  v2.get(`/chapters/${encodeURIComponent(chapterId)}/notes`, { params: preview ? { preview: 1 } : {} })
    .then((r) => r.data);
// Search (C4.4)
export const searchNotes = (q) => v2.get('/search', { params: { q } }).then((r) => r.data);
// Highlights + stick-ons (C5.1)
export const addHighlight = (body) => v2.post('/highlights', body).then((r) => r.data);
export const removeHighlight = (id) => v2.delete(`/highlights/${id}`).then((r) => r.data);
export const addSticky = (sectionCode, body) =>
  v2.post('/stickies', { section_code: sectionCode, body }).then((r) => r.data);
export const editSticky = (id, body) => v2.put(`/stickies/${id}`, { body }).then((r) => r.data);
export const removeSticky = (id) => v2.delete(`/stickies/${id}`).then((r) => r.data);
// Dictionary (C5.2): live=0 → only words already in the dictionary (no AI lookup)
export const lookupWord = (word, chapterId, { live = true } = {}) =>
  v2.get('/dictionary', { params: { word, chapter_id: chapterId || undefined, live: live ? 1 : 0 } })
    .then((r) => r.data);

// Tests (C7.3 engine, C7.4 screens)
export const getTestChapters = () => v2.get('/tests/chapters').then((r) => r.data);
export const startPractice = (chapterIds, count, pyqOnly = false) =>
  v2.post('/tests/practice', { chapter_ids: chapterIds, count, pyq_only: !!pyqOnly }).then((r) => r.data);
export const startChapterTest = (chapterId, kinds, pyqOnly = false) =>
  v2.post('/tests/chapter', { chapter_id: chapterId, kinds, pyq_only: !!pyqOnly }).then((r) => r.data);
export const getOpenTest = () => v2.get('/tests/open').then((r) => r.data);
export const getTestPaper = (testId) =>
  v2.get(`/tests/${encodeURIComponent(testId)}`).then((r) => r.data);
export const saveTestAnswer = (testId, questionId, answer, timeSpentSeconds) =>
  v2.patch(`/tests/${encodeURIComponent(testId)}/answer`, {
    question_id: questionId, answer, time_spent_seconds: timeSpentSeconds,
  }).then((r) => r.data);
export const finishTest = (testId) =>
  v2.post(`/tests/${encodeURIComponent(testId)}/finish`).then((r) => r.data);
export const getTestResult = (testId) =>
  v2.get(`/tests/${encodeURIComponent(testId)}/result`).then((r) => r.data);
export const getChapterStatus = (chapterId) =>
  v2.get(`/chapter-status/${encodeURIComponent(chapterId)}`).then((r) => r.data);
export const resetChapter = (chapterId) =>
  v2.post(`/chapter-reset/${encodeURIComponent(chapterId)}`).then((r) => r.data);

// Revision (C8.1)
export const getRevisionChapters = () => v2.get('/revision/chapters').then((r) => r.data);
export const getOpenRevision = () => v2.get('/revision/open').then((r) => r.data);
export const startRevision = (chapterIds) =>
  v2.post('/revision/start', { chapter_ids: chapterIds }).then((r) => r.data);
export const getRevision = (id) => v2.get(`/revision/${encodeURIComponent(id)}`).then((r) => r.data);
export const nextRevisionItem = (id) =>
  v2.post(`/revision/${encodeURIComponent(id)}/next`).then((r) => r.data);
export const answerRevision = (id, body) =>
  v2.post(`/revision/${encodeURIComponent(id)}/answer`, body).then((r) => r.data);
export const stopRevision = (id) =>
  v2.post(`/revision/${encodeURIComponent(id)}/stop`).then((r) => r.data);

// "Linux x86_64 · Chrome" — shown to the admin in the devices list.
export function deviceName() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  let browser = 'Browser';
  if (/Electron\//.test(ua)) browser = 'Desktop app';
  else if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  const platform = (typeof navigator !== 'undefined' && navigator.platform) || 'Computer';
  return `${platform} · ${browser}`;
}
